import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BizButton } from '../../../Core/Components/Mini/BizButton';
import { BizCheckBox } from '../../../Core/Components/Mini/BizCheckBox';
import BizInput from '../../../Core/Components/Mini/BizInput';
import { useGlobalState } from '../../../Core/Contexts/globalStateContext';
import {
	useGetTravelReports,
	useGetTravelReportTypes,
	useGetTravelExpenseSettings,
	useDeleteTravelReport,
	useSubmitTravelReport,
	useGetExpenseFeatureSettings,
} from '../../../Core/Hooks/useTravelReport';
import { TravelReport, TravelReportType } from '../../../Core/Types/admin/timereport';
import { formatLocalDate } from '../../../Core/Utils/dateUtils';
import TravelExpenseForm from '../Travel/TravelExpenseForm';
import ExpenseForm from './ExpenseForm';
import './ExpenseMain.scss';

const getMonthStart = (): string => {
	const d = new Date();
	return formatLocalDate(new Date(d.getFullYear(), d.getMonth(), 1));
};

const getMonthEnd = (): string => {
	const d = new Date();
	return formatLocalDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
};

const getStatusClass = (item: TravelReport): string => {
	if (item.isAttested) return 'attested';
	if (item.isSubmitted) return 'submitted';
	return 'draft';
};

const ExpenseMain = () => {
	const { t } = useTranslation();
	const [startDate, setStartDate] = useState(getMonthStart);
	const [endDate, setEndDate] = useState(getMonthEnd);

	const { showModal, hideModal } = useGlobalState();

	const { travelReports, statusState } = useGetTravelReports(startDate, endDate);
	const { travelReportTypes } = useGetTravelReportTypes();
	const { travelExpenseSettings } = useGetTravelExpenseSettings();
	const { remove } = useDeleteTravelReport();
	const { submit } = useSubmitTravelReport();
	const { expenseFeatureSettings } = useGetExpenseFeatureSettings();

	const [selectedTravelIds, setSelectedTravelIds] = useState<Set<number>>(new Set());
	const [selectedExpenseIds, setSelectedExpenseIds] = useState<Set<number>>(new Set());

	const kmTypeId = travelExpenseSettings?.travelExpenseKmReportTypeId ?? 0;

	const typeNameMap = useMemo(() => {
		const map = new Map<number, string>();
		travelReportTypes?.forEach((t: TravelReportType) => map.set(t.id, t.name));
		return map;
	}, [travelReportTypes]);

	const { travelReports: travelList, expenseReports: expenseList } = useMemo(() => {
		const travel: TravelReport[] = [];
		const expenses: TravelReport[] = [];
		for (const r of travelReports ?? []) {
			if (r.travelReportTypeId === kmTypeId) {
				travel.push(r);
			} else {
				expenses.push(r);
			}
		}
		travel.sort((a, b) => b.date.localeCompare(a.date));
		expenses.sort((a, b) => b.date.localeCompare(a.date));
		return { travelReports: travel, expenseReports: expenses };
	}, [travelReports, kmTypeId]);

	const draftTravelIds = useMemo(() => travelList.filter((r) => !r.isSubmitted).map((r) => r.id), [travelList]);

	const draftExpenseIds = useMemo(() => expenseList.filter((r) => !r.isSubmitted).map((r) => r.id), [expenseList]);

	const toggleTravelId = (id: number) => {
		setSelectedTravelIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleExpenseId = (id: number) => {
		setSelectedExpenseIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleAllTravel = (checked: boolean) => {
		setSelectedTravelIds(checked ? new Set(draftTravelIds) : new Set());
	};

	const toggleAllExpenses = (checked: boolean) => {
		setSelectedExpenseIds(checked ? new Set(draftExpenseIds) : new Set());
	};

	const handleBulkSubmit = (ids: Set<number>) => {
		ids.forEach((id) => submit(id));
		setSelectedTravelIds(new Set());
		setSelectedExpenseIds(new Set());
	};

	const handleBulkDelete = (ids: Set<number>) => {
		ids.forEach((id) => remove(id));
		setSelectedTravelIds(new Set());
		setSelectedExpenseIds(new Set());
	};

	const handleEditTravel = (report: TravelReport) => {
		showModal(
			<TravelExpenseForm existingReport={report} onClose={() => hideModal()} />,
			t('TIMEREPORT_USER.EXPENSE.MODALS.EDIT_TRAVEL_REPORT')
		);
	};

	const handleEditExpense = (report: TravelReport) => {
		showModal(
			<ExpenseForm existingReport={report} onClose={() => hideModal()} />,
			t('TIMEREPORT_USER.EXPENSE.MODALS.EDIT_EXPENSE')
		);
	};

	const handleAddTravel = () => {
		showModal(
			<TravelExpenseForm existingReport={null} onClose={() => hideModal()} />,
			t('TIMEREPORT_USER.EXPENSE.MODALS.ADD_TRAVEL_REPORT')
		);
	};

	const handleAddExpense = () => {
		showModal(
			<ExpenseForm existingReport={null} onClose={() => hideModal()} />,
			t('TIMEREPORT_USER.EXPENSE.MODALS.ADD_EXPENSE')
		);
	};

	const allTravelSelected = draftTravelIds.length > 0 && draftTravelIds.every((id) => selectedTravelIds.has(id));
	const allExpensesSelected = draftExpenseIds.length > 0 && draftExpenseIds.every((id) => selectedExpenseIds.has(id));
	const hasSelectedTravel = selectedTravelIds.size > 0;
	const hasSelectedExpenses = selectedExpenseIds.size > 0;

	const getStatusText = (item: TravelReport): string => {
		if (item.isAttested) return t('TIMEREPORT_USER.EXPENSE.STATUS.ATTESTED');
		if (item.isSubmitted) return t('TIMEREPORT_USER.EXPENSE.STATUS.SUBMITTED');
		return t('TIMEREPORT_USER.EXPENSE.STATUS.DRAFT');
	};

	return (
		<div className='biz-expense-main'>
			<div className='biz-expense-main__actions'>
				{/* <BizButton
					text='Add Travel Report'
					onClick={() => { setEditingReport(null); setFormMode('travel'); }}
				/>
				<BizButton
					text='Add Expense'
					onClick={() => { setEditingReport(null); setFormMode('expense'); }}
					icon='fa-solid fa-plus'
				/> */}
				<div>
					<label className='biz-expense-main__filter-label'>{t('TIMEREPORT_USER.EXPENSE.MAIN.FILTERS.FROM')}</label>
					<BizInput type='date' value={startDate} onChange={(e) => setStartDate(e.target.value)} />
				</div>
				<div>
					<label className='biz-expense-main__filter-label'>{t('TIMEREPORT_USER.EXPENSE.MAIN.FILTERS.TO')}</label>
					<BizInput type='date' value={endDate} onChange={(e) => setEndDate(e.target.value)} />
				</div>
			</div>

			{statusState === 'LOADING' && <p>{t('TIMEREPORT_USER.EXPENSE.MAIN.STATE.LOADING')}</p>}

			{/* Mileage reimbursement list */}
			{expenseFeatureSettings?.enabled && expenseFeatureSettings.mileageReimbursementEnabled && (
				<div className='biz-expense-main__section'>
					<div className='biz-expense-main__section-header'>
						<h4>{t('TIMEREPORT_USER.EXPENSE.MAIN.SECTIONS.TRAVEL_REPORTS')}</h4>
						<div className='biz-expense-main__bulk-actions'>
							<BizButton
								text={t('TIMEREPORT_USER.EXPENSE.MAIN.ACTIONS.SUBMIT_COUNT', { count: selectedTravelIds.size })}
								onClick={() => handleBulkSubmit(selectedTravelIds)}
								className='biz-expense-main__action-button biz-expense-main__action-button--submit'
								disabled={!hasSelectedTravel}
							/>
							<BizButton
								text={t('TIMEREPORT_USER.EXPENSE.MAIN.ACTIONS.DELETE_COUNT', { count: selectedTravelIds.size })}
								onClick={() => handleBulkDelete(selectedTravelIds)}
								className='biz-expense-main__action-button biz-expense-main__action-button--delete'
								disabled={!hasSelectedTravel}
							/>
						</div>
						<BizButton
							text={t('TIMEREPORT_USER.EXPENSE.MAIN.ACTIONS.ADD')}
							onClick={handleAddTravel}
							icon='fa-solid fa-plus'
							isLead={true}
							legacy={false}
							size='SMALL'
						/>
					</div>
					{travelList.length === 0 && statusState === 'SUCCESS' && (
						<div className='biz-expense-main__empty'>{t('TIMEREPORT_USER.EXPENSE.MAIN.EMPTY.NO_TRAVEL_REPORTS')}</div>
					)}
					{travelList.length > 0 && (
						<table className='biz-expense-main__table'>
							<thead>
								<tr>
									<th>
										<BizCheckBox value={allTravelSelected} onChange={toggleAllTravel} />
									</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.DATE')}</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.FROM')}</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.TO')}</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.DISTANCE')}</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.AMOUNT')}</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.STATUS')}</th>
								</tr>
							</thead>
							<tbody>
								{travelList.map((report) => (
									<tr key={report.id} onClick={() => handleEditTravel(report)}>
										<td onClick={(e) => e.stopPropagation()}>
											{!report.isSubmitted && (
												<BizCheckBox
													value={selectedTravelIds.has(report.id)}
													onChange={() => toggleTravelId(report.id)}
												/>
											)}
										</td>
										<td>{report.date.split('T')[0]}</td>
										<td>{report.fromLocation}</td>
										<td>{report.toLocation}</td>
										<td>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.DISTANCE_VALUE', { value: report.units })}</td>
										<td>{report.price}</td>
										<td>
											<span
												className={`biz-expense-main__status-badge biz-expense-main__status-badge--${getStatusClass(report)}`}
											>
												{getStatusText(report)}
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			)}

			{/* Generic Expenses List */}
			{expenseFeatureSettings?.enabled && expenseFeatureSettings.receiptExpensesEnabled && (
				<div className='biz-expense-main__section'>
					<div className='biz-expense-main__section-header'>
						<h4>{t('TIMEREPORT_USER.EXPENSE.MAIN.SECTIONS.EXPENSES')}</h4>
						<div className='biz-expense-main__bulk-actions'>
							<BizButton
								text={t('TIMEREPORT_USER.EXPENSE.MAIN.ACTIONS.SUBMIT_COUNT', { count: selectedExpenseIds.size })}
								onClick={() => handleBulkSubmit(selectedExpenseIds)}
								className='biz-expense-main__action-button biz-expense-main__action-button--submit'
								disabled={!hasSelectedExpenses}
								legacy={false}
							/>
							<BizButton
								text={t('TIMEREPORT_USER.EXPENSE.MAIN.ACTIONS.DELETE_COUNT', { count: selectedExpenseIds.size })}
								onClick={() => handleBulkDelete(selectedExpenseIds)}
								className='biz-expense-main__action-button biz-expense-main__action-button--delete'
								disabled={!hasSelectedExpenses}
								legacy={false}
							/>
						</div>
						<BizButton
							text={t('TIMEREPORT_USER.EXPENSE.MAIN.ACTIONS.ADD')}
							onClick={handleAddExpense}
							icon='fa-solid fa-plus'
							legacy={false}
							isLead={true}
						/>
					</div>
					{expenseList.length === 0 && statusState === 'SUCCESS' && (
						<div className='biz-expense-main__empty'>{t('TIMEREPORT_USER.EXPENSE.MAIN.EMPTY.NO_EXPENSES')}</div>
					)}
					{expenseList.length > 0 && (
						<table className='biz-expense-main__table'>
							<thead>
								<tr>
									<th>
										<BizCheckBox value={allExpensesSelected} onChange={toggleAllExpenses} />
									</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.DATE')}</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.TYPE')}</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.AMOUNT')}</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.VAT')}</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.COMMENT')}</th>
									<th>{t('TIMEREPORT_USER.EXPENSE.MAIN.TABLE.STATUS')}</th>
								</tr>
							</thead>
							<tbody>
								{expenseList.map((report) => (
									<tr key={report.id} onClick={() => handleEditExpense(report)}>
										<td onClick={(e) => e.stopPropagation()}>
											{!report.isSubmitted && (
												<BizCheckBox
													value={selectedExpenseIds.has(report.id)}
													onChange={() => toggleExpenseId(report.id)}
												/>
											)}
										</td>
										<td>{report.date.split('T')[0]}</td>
										<td>{typeNameMap.get(report.travelReportTypeId) ?? '—'}</td>
										<td>{report.price}</td>
										<td>{report.vat}</td>
										<td>{report.comment}</td>
										<td>
											<span
												className={`biz-expense-main__status-badge biz-expense-main__status-badge--${getStatusClass(report)}`}
											>
												{getStatusText(report)}
											</span>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			)}
		</div>
	);
};

export default ExpenseMain;
